/**
 * DAY-RUN interleave (P3.md "4-split UI", the killer feature): schedule +
 * travel + timetable merged into ONE chronological view per day, so the TM
 * reads the day top-to-bottom — never three tabs mentally zipped together.
 *
 * Pure data logic, no React: this is what the vitest suite locks down
 * (test/day-run.test.ts). All field/collection names come from the registry
 * by typed property access (CONSTRAINTS #5 — no name literals here).
 *
 * Ordering rules:
 *  - groups: by day ascending (YYYY-MM-DD sorts lexicographically); undated
 *    entries form a final "" group — visible, never dropped (CONSTRAINTS #3).
 *  - within a day: by time ascending; entries with no time sink to the end of
 *    their day (an untimed item is a reminder, not an anchor);
 *  - travel legs sort by DEPARTURE and live on their departure day — a
 *    red-eye landing D+1 stays on the day you leave, with `endDayOffset` = 1
 *    for the UI's "+1" marker (dateline legs can be negative);
 *  - ties (same day+time): travel first (a departure is the hardest
 *    constraint of the minute), then schedule, then timetable; then item id
 *    for full determinism.
 */
import { registry, type CollectionKey } from "@td/registry";
import { isItemDeleted, type Item } from "@td/sync-core";
import type { GigRecord } from "../db/idb.ts";

export interface DayRunEntry {
  collection: CollectionKey;
  itemId: string;
  item: Item;
  /** Group day (travel: departure day). "" = undated. */
  day: string;
  /** Sort time within the day (travel: departure time). "" = untimed. */
  time: string;
  /** End/arrival time, when known. */
  endTime: string;
  /** Whole days the end lies past `day` (red-eye +1, dateline can be < 0). */
  endDayOffset: number;
  /** Hotels appear twice — once per threshold moment (humans think in
   * check-in and check-out, not in date ranges). */
  hotelPhase?: "check_in" | "check_out";
}

export interface DayGroup {
  /** YYYY-MM-DD, or "" for the undated tail group. */
  day: string;
  entries: DayRunEntry[];
}

const DAY_RE = /^\d{4}-\d{2}-\d{2}$/;

function s(v: unknown): string {
  return typeof v === "string" ? v : "";
}

function wholeDayDiff(a: string, b: string): number {
  if (!DAY_RE.test(a) || !DAY_RE.test(b)) return 0;
  const [ay, am, ad] = a.split("-").map(Number) as [number, number, number];
  const [by, bm, bd] = b.split("-").map(Number) as [number, number, number];
  return Math.round((Date.UTC(by, bm - 1, bd) - Date.UTC(ay, am - 1, ad)) / 86_400_000);
}

/** Tie-break rank at equal day+time (see module note). */
function collectionRank(key: CollectionKey): number {
  if (key === registry.travel.key) return 0;
  if (key === registry.schedule.key) return 1;
  if (key === registry.hotels.key) return 2; // a checkout is a hard anchor too
  return 3; // timetable
}

function liveItems(gig: GigRecord, key: CollectionKey): [string, Item][] {
  return (Object.entries(gig.state.collections[key] ?? {}) as [string, Item][]).filter(
    ([, item]) => !isItemDeleted(item),
  );
}

const sv = (v: unknown): string => (typeof v === "string" ? v : "");

/** The entry's own short human label — title / act / from→to route / hotel name.
 * Registry-typed (CONSTRAINTS #5). Shared by GlanceHero + the native glance. */
export function entryLabel(e: DayRunEntry): string {
  const k = e.collection;
  if (k === registry.schedule.key) return sv(e.item.fields[registry.schedule.fields.title.key]);
  if (k === registry.timetable.key) return sv(e.item.fields[registry.timetable.fields.act.key]);
  if (k === registry.travel.key) {
    const tv = registry.travel.fields;
    const dep = sv(e.item.fields[tv.dep_place.key]) || sv(e.item.fields[tv.dep_iata.key]);
    const arr = sv(e.item.fields[tv.arr_place.key]) || sv(e.item.fields[tv.arr_iata.key]);
    return [dep, arr].filter(Boolean).join(" → ") || sv(e.item.fields[tv.kind.key]);
  }
  if (k === registry.hotels.key) return sv(e.item.fields[registry.hotels.fields.name.key]);
  return "";
}

/** The entry's place line (schedule place / timetable stage); "" otherwise. */
export function entryPlace(e: DayRunEntry): string {
  if (e.collection === registry.schedule.key) return sv(e.item.fields[registry.schedule.fields.place.key]);
  if (e.collection === registry.timetable.key) return sv(e.item.fields[registry.timetable.fields.stage.key]);
  return "";
}

export function compareEntries(a: DayRunEntry, b: DayRunEntry): number {
  // Undated group last; otherwise day ascending.
  if (a.day !== b.day) {
    if (a.day === "") return 1;
    if (b.day === "") return -1;
    return a.day < b.day ? -1 : 1;
  }
  // Untimed entries sink to the end of their day.
  if (a.time !== b.time) {
    if (a.time === "") return 1;
    if (b.time === "") return -1;
    return a.time < b.time ? -1 : 1;
  }
  const ra = collectionRank(a.collection);
  const rb = collectionRank(b.collection);
  if (ra !== rb) return ra - rb;
  return a.itemId < b.itemId ? -1 : a.itemId > b.itemId ? 1 : 0;
}

/** Every day-split entry of a gig, fully sorted (flat). */
export function buildDayRunEntries(gig: GigRecord): DayRunEntry[] {
  const entries: DayRunEntry[] = [];

  const sch = registry.schedule.fields;
  for (const [itemId, item] of liveItems(gig, registry.schedule.key)) {
    entries.push({
      collection: registry.schedule.key,
      itemId,
      item,
      day: s(item.fields[sch.day.key]),
      time: s(item.fields[sch.start_time.key]),
      endTime: s(item.fields[sch.end_time.key]),
      endDayOffset: 0,
    });
  }

  const tt = registry.timetable.fields;
  for (const [itemId, item] of liveItems(gig, registry.timetable.key)) {
    entries.push({
      collection: registry.timetable.key,
      itemId,
      item,
      day: s(item.fields[tt.day.key]),
      time: s(item.fields[tt.start_time.key]),
      endTime: s(item.fields[tt.end_time.key]),
      endDayOffset: 0,
    });
  }

  // Hotels: one entry per threshold moment. check_in/check_out are
  // "YYYY-MM-DD HH:MM" (venue-local) — split into the day-run's day+time.
  const ho = registry.hotels.fields;
  const splitLocal = (v: string): { day: string; time: string } => {
    const m = /^(\d{4}-\d{2}-\d{2})[ T]?([0-2]?\d:[0-5]\d)?/.exec(v);
    return { day: m?.[1] ?? "", time: m?.[2] ?? "" };
  };
  for (const [itemId, item] of liveItems(gig, registry.hotels.key)) {
    for (const phase of ["check_in", "check_out"] as const) {
      const raw = s(item.fields[ho[phase].key]);
      if (raw === "") continue;
      const { day, time } = splitLocal(raw);
      if (day === "") continue; // dateless hotel moments have no day to live on
      entries.push({
        collection: registry.hotels.key,
        itemId: `${itemId}:${phase}`,
        item,
        day,
        time,
        endTime: "",
        endDayOffset: 0,
        hotelPhase: phase,
      });
    }
  }

  const tv = registry.travel.fields;
  // v1 tolerance: some imported legs carry a full datetime IN the time field
  // ("2026-09-14T11:45") with the day field empty — split for display so the
  // leg lives on its real day and reads "11:45", never a raw ISO string.
  const normDayTime = (day: string, time: string): { day: string; time: string } => {
    if (/^\d{4}-\d{2}-\d{2}[T ]/.test(time)) {
      const p = splitLocal(time);
      return { day: day !== "" ? day : p.day, time: p.time };
    }
    return { day, time };
  };
  for (const [itemId, item] of liveItems(gig, registry.travel.key)) {
    const dep = normDayTime(s(item.fields[tv.dep_day.key]), s(item.fields[tv.dep_time.key]));
    const arr = normDayTime(s(item.fields[tv.arr_day.key]), s(item.fields[tv.arr_time.key]));
    entries.push({
      collection: registry.travel.key,
      itemId,
      item,
      day: dep.day,
      time: dep.time,
      endTime: arr.time,
      endDayOffset: arr.day !== "" && dep.day !== "" ? wholeDayDiff(dep.day, arr.day) : 0,
    });
  }

  entries.sort(compareEntries);
  return entries;
}

/** The day-run: sorted entries grouped under day headers (undated last). */
export function buildDayRun(gig: GigRecord): DayGroup[] {
  const groups: DayGroup[] = [];
  for (const entry of buildDayRunEntries(gig)) {
    const last = groups[groups.length - 1];
    if (last !== undefined && last.day === entry.day) last.entries.push(entry);
    else groups.push({ day: entry.day, entries: [entry] });
  }
  return groups;
}

/**
 * A person_id rendered to a human is a NAME, never a raw item id ("crew-laurie"
 * on a briefing line is exactly the noise ADR-014's deletion test bans).
 * Resolves through the gig's crew rows; an id with no matching crew row renders
 * as-is — honest and greppable, never silently hidden.
 */
export function personDisplayName(gig: GigRecord, personId: string): string {
  if (personId === "") return "";
  const row = gig.state.collections[registry.crew.key]?.[personId];
  if (row === undefined || isItemDeleted(row)) return personId;
  const name = s(row.fields[registry.crew.fields.name.key]) || s(row.fields[registry.crew.fields.stage_name.key]);
  return name !== "" ? name : personId;
}

// ---------------------------------------------------------------------------
// Journey narration (2026-07-02, Eduardo: "this app is for humans") — connecting
// legs speak their gaps. Pure logic; the renderer draws a quiet line between
// two travel rows instead of leaving the TM to do airport math.

/** The gap after a travel leg, when the NEXT leg connects to it. */
export interface LayoverInfo {
  /** Minutes between this leg's arrival and the next leg's departure. */
  minutes: number;
  /** Where the wait happens (the arrival place of this leg). */
  place: string;
  /** True when the connection crosses into another day (sleep happens). */
  overnight: boolean;
  /** A hotel whose check-in falls inside the gap — the human answer to
   * "where do I sleep": name + the checkout moment that ends the layover. */
  hotelName?: string;
  hotelCheckout?: string;
}

// Canonical strict HH:MM (venue-local wall time, ADR-006) — same shape as
// travelWindow.ts's TIME_RE. Tightened 2026-07-04: the old /^([0-2]?\d):([0-5]\d)$/
// let single-digit hours AND "29:59" through ("9:00", "24:00" both matched).
const STRICT_TIME_RE = /^([01]\d|2[0-3]):([0-5]\d)$/;

/** Minutes since the UTC epoch for a venue-local YYYY-MM-DD/HH:MM pair, treated
 * as if it were UTC (a stable sortable/diffable instant — see annotateJourneys
 * and gaps.ts's buildGaps, which both diff two of these). Null on anything
 * malformed — never guesses. Exported for gaps.ts (CONSTRAINTS: one source of
 * truth for this math, not a second copy). */
export function minutesOf(day: string, time: string): number | null {
  const m = STRICT_TIME_RE.exec(time);
  if (!DAY_RE.test(day) || m === null) return null;
  const [y, mo, d] = day.split("-").map(Number) as [number, number, number];
  return Math.round(Date.UTC(y, mo - 1, d, Number(m[1]), Number(m[2])) / 60_000);
}

function placeKey(place: string, iata: string): string {
  if (iata !== "") return `#${iata.toLowerCase()}`;
  return place.trim().toLowerCase();
}

/**
 * For each travel entry (in an already-sorted entry list), the layover that
 * FOLLOWS it — present only when the next leg of the same party genuinely
 * connects: same person (or whole-party), departs where this one lands,
 * within 24h. Hotels of the gig are matched into overnight gaps by check-in
 * time. Returns a map keyed by the travel entry's itemId.
 */
export function annotateJourneys(gig: GigRecord, entries: readonly DayRunEntry[]): Map<string, LayoverInfo> {
  const tv = registry.travel.fields;
  const ho = registry.hotels.fields;
  const out = new Map<string, LayoverInfo>();
  const legs = entries.filter((e) => e.collection === registry.travel.key);

  // Hotel stays, parsed once: [checkInMin, checkOutMin, name, checkoutTime].
  const stays = (Object.values(gig.state.collections[registry.hotels.key] ?? {}) as Item[])
    .filter((h) => !isItemDeleted(h))
    .map((h) => {
      const ci = /^(\d{4}-\d{2}-\d{2})[ T]?([0-2]?\d:[0-5]\d)?/.exec(s(h.fields[ho.check_in.key]) ?? "");
      const co = /^(\d{4}-\d{2}-\d{2})[ T]?([0-2]?\d:[0-5]\d)?/.exec(s(h.fields[ho.check_out.key]) ?? "");
      return {
        name: s(h.fields[ho.name.key]),
        inMin: ci ? minutesOf(ci[1]!, ci[2] ?? "15:00") : null, // sensible default check-in
        outTime: co?.[2] ?? "",
      };
    })
    .filter((h) => h.name !== "" && h.inMin !== null);

  for (let i = 0; i < legs.length - 1; i++) {
    const a = legs[i]!;
    const b = legs[i + 1]!;
    const personA = s(a.item.fields[tv.person_id.key]);
    const personB = s(b.item.fields[tv.person_id.key]);
    if (personA !== "" && personB !== "" && personA !== personB) continue; // different traveler
    const arrPlace = s(a.item.fields[tv.arr_place.key]);
    const link =
      placeKey(arrPlace, s(a.item.fields[tv.arr_iata.key])) ===
      placeKey(s(b.item.fields[tv.dep_place.key]), s(b.item.fields[tv.dep_iata.key]));
    if (!link || arrPlace === "" && s(a.item.fields[tv.arr_iata.key]) === "") continue;
    const arrDay = s(a.item.fields[tv.arr_day.key]) || a.day;
    const arrMin = minutesOf(arrDay, a.endTime);
    const depMin = minutesOf(b.day, b.time);
    if (arrMin === null || depMin === null) continue;
    const gap = depMin - arrMin;
    if (gap <= 0 || gap > 24 * 60) continue; // not a connection
    const info: LayoverInfo = {
      minutes: gap,
      place: arrPlace || s(a.item.fields[tv.arr_iata.key]).toUpperCase(),
      overnight: b.day !== arrDay,
    };
    if (info.overnight) {
      // Where do I sleep: a hotel whose check-in sits inside the gap.
      const stay = stays.find((h) => h.inMin! >= arrMin && h.inMin! <= depMin);
      if (stay !== undefined) {
        info.hotelName = stay.name;
        info.hotelCheckout = stay.outTime;
      }
    }
    out.set(a.itemId, info);
  }
  return out;
}

/** "2h10" — durations read like a pilot says them, no words needed. */
export function formatDuration(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m} min`;
  return m === 0 ? `${h}h` : `${h}h${String(m).padStart(2, "0")}`;
}
